import api from './api';
import {
  ScholarshipCampaign,
  ScholarshipCandidateQuestion,
  ScholarshipCertificate,
  ScholarshipCreatePayload,
  ScholarshipExamAccessPayload,
  ScholarshipExamAccessResponse,
  ScholarshipFinalResults,
  ScholarshipInterview,
  ScholarshipRegistration,
  ScholarshipRegistrationPayload,
  ScholarshipStartResponse,
  ScholarshipSubmitResponse,
} from '../types/scholarship';

export interface AddScholarshipQuestionPayload {
  questionText: string;
  questionType?: 'MCQ';
  marks: number;
  difficulty?: 'EASY' | 'MEDIUM' | 'HARD';
  options: Array<{ text: string; isCorrect: boolean; explanation?: string }>;
  explanation?: string;
  tags?: string[];
}

const noCacheConfig = (params: Record<string, string | number | undefined> = {}) => ({
  params: {
    ...params,
    _ts: Date.now(),
  },
});

export const scholarshipService = {
  listScholarships: async (): Promise<ScholarshipCampaign[]> => {
    const response = await api.get('/scholarships', noCacheConfig());
    return response.data;
  },

  getScholarship: async (scholarshipId: string): Promise<ScholarshipCampaign> => {
    const response = await api.get(`/scholarships/${scholarshipId}`, noCacheConfig());
    return response.data;
  },

  register: async (payload: ScholarshipRegistrationPayload): Promise<ScholarshipRegistration> => {
    const response = await api.post('/scholarships/register', payload);
    return response.data;
  },

  getMyRegistrations: async (): Promise<ScholarshipRegistration[]> => {
    const response = await api.get('/scholarships/me/registrations');
    return response.data;
  },

  accessExam: async (payload: ScholarshipExamAccessPayload): Promise<ScholarshipExamAccessResponse> => {
    const response = await api.post('/exams/access', payload);
    return response.data;
  },

  startExam: async (payload: {
    scholarshipId?: string;
    registrationId?: string;
  }): Promise<ScholarshipStartResponse> => {
    const response = await api.post('/exams/start', payload);
    return response.data;
  },

  startExamWithAccess: async (payload: ScholarshipExamAccessPayload): Promise<ScholarshipStartResponse> => {
    const response = await api.post('/exams/start-access', payload);
    return response.data;
  },

  submitExam: async (payload: {
    attemptId: string;
    totalTimeSpent?: number;
    answers: Array<{ questionId: string; selectedOptionId?: string; answerText?: string; timeSpentSeconds?: number }>;
  }): Promise<ScholarshipSubmitResponse> => {
    const response = await api.post('/exams/submit', payload);
    return response.data;
  },

  submitExamWithAccess: async (payload: {
    attemptId: string;
    registrationId: string;
    studentPhone: string;
    totalTimeSpent?: number;
    answers: Array<{ questionId: string; selectedOptionId?: string; answerText?: string; timeSpentSeconds?: number }>;
  }): Promise<ScholarshipSubmitResponse> => {
    const response = await api.post('/exams/submit-access', payload);
    return response.data;
  },

  getFinalResults: async (scholarshipId?: string): Promise<ScholarshipFinalResults> => {
    const response = await api.get('/results/final', noCacheConfig({ scholarshipId }));
    return response.data;
  },

  getCertificate: async (registrationId: string): Promise<ScholarshipCertificate> => {
    const response = await api.get(`/scholarships/registrations/${registrationId}/certificate`);
    return response.data;
  },

  getAdminOverview: async (): Promise<ScholarshipCampaign[]> => {
    const response = await api.get('/scholarships/admin/overview', noCacheConfig());
    return response.data;
  },

  createScholarship: async (payload: ScholarshipCreatePayload): Promise<ScholarshipCampaign> => {
    const response = await api.post('/scholarships', payload);
    return response.data;
  },

  addQuestion: async (
    scholarshipId: string,
    payload: AddScholarshipQuestionPayload,
  ): Promise<ScholarshipCandidateQuestion> => {
    const response = await api.post(`/scholarships/${scholarshipId}/questions`, payload);
    return response.data;
  },

  getParticipants: async (scholarshipId: string): Promise<ScholarshipRegistration[]> => {
    const response = await api.get(`/scholarships/${scholarshipId}/participants`, noCacheConfig());
    return response.data;
  },

  shortlist: async (
    scholarshipId: string,
    payload: { topN?: number; minPercentage?: number },
  ): Promise<ScholarshipRegistration[]> => {
    const response = await api.post(`/scholarships/${scholarshipId}/shortlist`, payload);
    return response.data;
  },

  scheduleInterview: async (payload: {
    scholarshipId: string;
    registrationId: string;
    scheduledAt: string;
    mode?: 'ONLINE' | 'PHONE' | 'IN_PERSON';
    meetingLink?: string;
    interviewerName?: string;
    notes?: string;
  }): Promise<ScholarshipInterview> => {
    const response = await api.post('/interviews', payload);
    return response.data;
  },

  declareWinners: async (scholarshipId: string): Promise<ScholarshipFinalResults> => {
    const response = await api.post(`/scholarships/${scholarshipId}/declare-winners`, {});
    return response.data;
  },

  generateCertificates: async (scholarshipId: string): Promise<ScholarshipCertificate[]> => {
    const response = await api.post(`/scholarships/${scholarshipId}/certificates`);
    return response.data;
  },
};

export default scholarshipService;
